import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, TrendingUp, Eye, ShoppingCart, CreditCard, CheckCircle2,
  Users, RefreshCw, Package, AlertTriangle, Globe, Zap, DollarSign,
  Clock, ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===================== FUNNEL BAR =====================

function FunnelBar({ funnel }) {
  if (!funnel || funnel.length === 0) return <p className="text-sm text-gray-400">Aguardando dados...</p>;

  const maxVal = Math.max(...funnel.map(f => f.sessions), 1);

  const stages = [
    { key: 'page_view', label: 'Visitantes', icon: Eye, gradient: 'from-slate-500 to-slate-600' },
    { key: 'product_view', label: 'Viram Produto', icon: Package, gradient: 'from-blue-500 to-blue-600' },
    { key: 'add_to_cart', label: 'Carrinho', icon: ShoppingCart, gradient: 'from-amber-500 to-amber-600' },
    { key: 'checkout_started', label: 'Checkout', icon: CreditCard, gradient: 'from-violet-500 to-violet-600' },
    { key: 'purchase_completed', label: 'Compra', icon: CheckCircle2, gradient: 'from-emerald-500 to-emerald-600' },
  ];

  return (
    <div className="space-y-2.5" data-testid="dashboard-funnel">
      {stages.map((stage, i) => {
        const data = funnel.find(f => f.stage === stage.key);
        const count = data?.sessions || 0;
        const pct = maxVal > 0 ? (count / maxVal) * 100 : 0;
        const prevCount = i > 0 ? (funnel.find(f => f.stage === stages[i - 1].key)?.sessions || 0) : 0;
        const convRate = i > 0 && prevCount > 0 ? ((count / prevCount) * 100).toFixed(0) : null;
        const Icon = stage.icon;

        return (
          <div key={stage.key} className="flex items-center gap-3">
            <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${stage.gradient} flex items-center justify-center shrink-0`}>
              <Icon className="h-3.5 w-3.5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs text-gray-600">{stage.label}</span>
                <div className="flex items-center gap-2">
                  {convRate && (
                    <span className="text-[10px] text-gray-400">{convRate}%</span>
                  )}
                  <span className="text-xs font-semibold text-gray-900 tabular-nums">{count.toLocaleString('pt-BR')}</span>
                </div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5">
                <div className={`bg-gradient-to-r ${stage.gradient} h-1.5 rounded-full transition-all duration-700`} style={{ width: `${Math.max(pct, 1)}%` }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===================== DAILY SESSIONS CHART =====================

function SessionsChart({ data }) {
  if (!data || data.length === 0) return <p className="text-sm text-gray-400">Sem dados</p>;

  const maxSessions = Math.max(...data.map(d => d.sessions), 1);
  const totalSessions = data.reduce((s, d) => s + d.sessions, 0);

  return (
    <div data-testid="dashboard-sessions-chart">
      <div className="flex items-end gap-[2px] h-28">
        {data.map((day, i) => {
          const h = maxSessions > 0 ? (day.sessions / maxSessions) * 100 : 0;
          const isToday = i === data.length - 1;
          return (
            <div key={day.date} className="flex-1 flex flex-col items-center justify-end group relative">
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                {day.date?.slice(5)}: {day.sessions}
              </div>
              <div
                className={`w-full rounded-sm transition-all duration-300 ${isToday ? 'bg-emerald-500' : 'bg-slate-300 hover:bg-slate-400'}`}
                style={{ height: `${Math.max(h, 3)}%`, minHeight: '2px' }}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[10px] text-gray-400">{data[0]?.date?.slice(5)}</span>
        <span className="text-[10px] text-gray-500 font-medium">{totalSessions} sessoes</span>
        <span className="text-[10px] text-gray-400">Hoje</span>
      </div>
    </div>
  );
}

// ===================== STATUS BADGE =====================

function MiniStatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  if (s === 'approved' || s === 'paid') return <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Pago</Badge>;
  if (s === 'pending') return <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Pendente</Badge>;
  if (s === 'cancelled' || s === 'rejected') return <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Cancelado</Badge>;
  return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
}

// ===================== MAIN DASHBOARD =====================

export default function UnifiedDashboard() {
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [liveData, setLiveData] = useState(null);
  const [abandonedData, setAbandonedData] = useState(null);
  const [pagesData, setPagesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [activeTab, setActiveTab] = useState('resumo');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, overviewRes, topRes, liveRes, abandonedRes, pagesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/analytics/overview?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics/top-products?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics/live`, { withCredentials: true }),
        axios.get(`${API}/analytics/abandoned-carts?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics/pages?days=${period}`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setOverview(overviewRes.data);
      setTopProducts(topRes.data);
      setLiveData(liveRes.data);
      setAbandonedData(abandonedRes.data);
      setPagesData(pagesRes.data);
    } catch (e) {
      console.error('Dashboard error:', e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh live data
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/analytics/live`, { withCredentials: true });
        setLiveData(res.data);
      } catch (_) {}
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div data-testid="admin-dashboard" className="space-y-4">
        <h1 className="text-2xl font-bold text-gray-900 font-['Playfair_Display']">Dashboard</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="bg-white rounded-xl p-5 shadow-sm border animate-pulse h-24" />)}
        </div>
      </div>
    );
  }

  const revenue = stats?.total_revenue || 0;
  const orders = stats?.total_orders || 0;
  const todaySessions = overview?.today_sessions || 0;
  const totalSessions = overview?.total_sessions || 0;
  const eventCounts = overview?.event_counts || {};
  const purchases = eventCounts.purchase_completed || 0;
  const conversionRate = totalSessions > 0 ? ((purchases / totalSessions) * 100).toFixed(1) : '0.0';

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Playfair_Display']">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Visao completa da sua loja</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Live indicator - always visible */}
          {liveData && (
            <div className={`flex items-center gap-1.5 border rounded-lg px-3 py-1.5 ${liveData.active_sessions > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`} data-testid="live-indicator">
              {liveData.active_sessions > 0 ? (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
              ) : (
                <span className="flex h-2 w-2">
                  <span className="inline-flex rounded-full h-2 w-2 bg-gray-300" />
                </span>
              )}
              <span className={`text-xs font-medium ${liveData.active_sessions > 0 ? 'text-emerald-700' : 'text-gray-500'}`}>{liveData.active_sessions} online</span>
            </div>
          )}
          <div className="flex items-center bg-white rounded-lg border px-0.5 py-0.5">
            {[7, 14, 30].map(d => (
              <button key={d} onClick={() => setPeriod(d)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${period === d ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800'}`}
                data-testid={`period-${d}d`}>
                {d}d
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="h-8 w-8 p-0" data-testid="refresh-dashboard">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid="kpi-receita">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-4 w-4 text-emerald-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500">Receita Total</p>
          <p className="text-xl font-bold text-gray-900">R$ {revenue.toFixed(2).replace('.', ',')}</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid="kpi-pedidos">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="h-4 w-4 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500">Pedidos</p>
          <p className="text-xl font-bold text-gray-900">{orders}</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid="kpi-visitantes">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 text-violet-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500">Visitantes ({period}d)</p>
          <p className="text-xl font-bold text-gray-900">{totalSessions.toLocaleString('pt-BR')}</p>
          <p className="text-[10px] text-emerald-600 font-medium mt-0.5">{todaySessions} hoje</p>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100" data-testid="kpi-conversao">
          <div className="flex items-center justify-between mb-2">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-amber-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500">Taxa Conversao</p>
          <p className="text-xl font-bold text-gray-900">{conversionRate}%</p>
          <p className="text-[10px] text-gray-400 font-medium mt-0.5">{purchases} compras</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="resumo" data-testid="tab-resumo">Resumo</TabsTrigger>
          <TabsTrigger value="produtos" data-testid="tab-produtos">Produtos</TabsTrigger>
          <TabsTrigger value="carrinhos" data-testid="tab-carrinhos">Carrinhos</TabsTrigger>
          <TabsTrigger value="paginas" data-testid="tab-paginas">Paginas</TabsTrigger>
        </TabsList>

        {/* ===== RESUMO TAB ===== */}
        <TabsContent value="resumo" className="space-y-6 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Funnel */}
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <h2 className="text-sm font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-gray-400" /> Funil de Conversao
              </h2>
              <FunnelBar funnel={overview?.funnel || []} />
            </div>

            {/* Sessions Chart */}
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <h2 className="text-sm font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-gray-400" /> Sessoes ({period} dias)
              </h2>
              <SessionsChart data={overview?.daily_sessions || []} />
            </div>
          </div>

          {/* Recent Orders */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-800">Ultimos Pedidos</h2>
              <Badge variant="outline" className="text-[10px]">{stats?.recent_orders?.length || 0}</Badge>
            </div>
            {stats?.recent_orders?.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/50">
                      <TableHead className="text-xs">Pedido</TableHead>
                      <TableHead className="text-xs">Cliente</TableHead>
                      <TableHead className="text-xs text-right">Total</TableHead>
                      <TableHead className="text-xs text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.recent_orders.slice(0, 8).map((order) => (
                      <TableRow key={order.order_id}>
                        <TableCell className="font-mono text-[11px] text-gray-500">{(order.order_id || '').slice(0, 12)}...</TableCell>
                        <TableCell className="text-xs font-medium">{order.customer_name}</TableCell>
                        <TableCell className="text-xs font-semibold text-right">R$ {(order.total || 0).toFixed(2).replace('.', ',')}</TableCell>
                        <TableCell className="text-center"><MiniStatusBadge status={order.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-400 text-sm">Nenhum pedido ainda</div>
            )}
          </div>

          {/* Event Breakdown */}
          {Object.keys(eventCounts).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
              <h2 className="text-sm font-semibold mb-3 text-gray-800">Eventos Registrados ({period}d)</h2>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {Object.entries(eventCounts).map(([event, count]) => (
                  <div key={event} className="bg-gray-50 rounded-lg p-2.5 text-center">
                    <p className="text-base font-bold text-gray-900 tabular-nums">{count.toLocaleString('pt-BR')}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{event.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ===== PRODUTOS TAB ===== */}
        <TabsContent value="produtos" className="mt-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <Package className="h-4 w-4 text-gray-400" /> Top Produtos ({period} dias)
              </h2>
            </div>
            {topProducts.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/50">
                      <TableHead className="text-xs">#</TableHead>
                      <TableHead className="text-xs">Produto</TableHead>
                      <TableHead className="text-xs text-center">Views</TableHead>
                      <TableHead className="text-xs text-center">Carrinho</TableHead>
                      <TableHead className="text-xs text-center">Compras</TableHead>
                      <TableHead className="text-xs text-center">Conv.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topProducts.map((p, i) => {
                      const convRate = p.views > 0 ? ((p.purchases / p.views) * 100).toFixed(1) : '0.0';
                      return (
                        <TableRow key={p.product_id} data-testid={`top-product-${i}`}>
                          <TableCell className="text-xs text-gray-400 font-medium">{i + 1}</TableCell>
                          <TableCell className="text-xs font-medium max-w-[200px] truncate">{p.name}</TableCell>
                          <TableCell className="text-center">
                            <span className="text-xs tabular-nums">{p.views}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-xs tabular-nums text-amber-600">{p.add_to_cart}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-xs tabular-nums text-emerald-600 font-semibold">{p.purchases}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`text-xs font-medium ${parseFloat(convRate) > 5 ? 'text-emerald-600' : parseFloat(convRate) > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{convRate}%</span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-400 text-sm">Nenhum dado ainda. Os dados aparecem conforme os visitantes navegam.</div>
            )}
          </div>
        </TabsContent>

        {/* ===== CARRINHOS ABANDONADOS TAB ===== */}
        <TabsContent value="carrinhos" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500 mb-1">Sessoes com Carrinho</p>
              <p className="text-xl font-bold text-gray-900">{abandonedData?.total_cart_sessions || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center gap-1.5 mb-1">
                <AlertTriangle className="h-3 w-3 text-red-500" />
                <p className="text-xs text-gray-500">Abandonados</p>
              </div>
              <p className="text-xl font-bold text-red-600">{abandonedData?.total_abandoned || 0}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500 mb-1">Taxa de Abandono</p>
              <p className="text-xl font-bold text-amber-600">{abandonedData?.abandonment_rate || 0}%</p>
            </div>
          </div>

          {abandonedData?.abandoned_details?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h2 className="text-sm font-semibold text-gray-800">Detalhes dos Carrinhos Abandonados</h2>
              </div>
              <div className="divide-y divide-gray-50">
                {abandonedData.abandoned_details.map((item) => (
                  <div key={item.session_id} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px] text-gray-400 font-mono">{item.session_id.slice(0, 20)}...</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {item.events.map((evt, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-gray-50 text-gray-600 border border-gray-100">
                          {evt.product_name || evt.event} {evt.product_price ? `R$ ${evt.product_price.toFixed(2).replace('.', ',')}` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ===== PAGINAS TAB ===== */}
        <TabsContent value="paginas" className="mt-4 space-y-4">
          {/* Live Sessions */}
          {liveData?.sessions?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-50 flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <h2 className="text-sm font-semibold text-gray-800">Ao Vivo ({liveData.active_sessions})</h2>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/50">
                      <TableHead className="text-xs">Sessao</TableHead>
                      <TableHead className="text-xs">Pagina</TableHead>
                      <TableHead className="text-xs">Evento</TableHead>
                      <TableHead className="text-xs text-center">Acoes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {liveData.sessions.map((s) => (
                      <TableRow key={s.session_id}>
                        <TableCell className="font-mono text-[10px] text-gray-500">{s.session_id.slice(0, 16)}...</TableCell>
                        <TableCell className="text-xs">{s.last_page || '-'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{(s.last_event || '').replace(/_/g, ' ')}</Badge></TableCell>
                        <TableCell className="text-center text-xs font-medium">{s.events}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Page Views */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-400" /> Paginas Mais Visitadas ({period}d)
              </h2>
            </div>
            {pagesData.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/50">
                      <TableHead className="text-xs">Pagina</TableHead>
                      <TableHead className="text-xs text-center">Views</TableHead>
                      <TableHead className="text-xs text-center">Visitantes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pagesData.map((pg, i) => (
                      <TableRow key={i} data-testid={`page-row-${i}`}>
                        <TableCell className="font-mono text-xs">{pg.page}</TableCell>
                        <TableCell className="text-center text-xs font-medium">{pg.views}</TableCell>
                        <TableCell className="text-center text-xs">{pg.unique_visitors}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-400 text-sm">Nenhum dado ainda</div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
